import { PropsWithChildren, ReactNode, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Box } from 'reflexbox';

import { Button } from './Button';

export type ExpandProps = PropsWithChildren<{
	preview?: number;
	button?: ReactNode;
}>;

/**
 * Alternative for the Expand component.
 * Shows a preview text with a button to expand entire text.
 * Shows expand button if preview is more than 3 lines
 * @param props props
 * @param props.preview Number of lines of text you want to be previewed (lh unit). Defaults to 3
 * @param props.button Optional: Overrides the default button implementation
 * @param props.children Text which will be wrapped
 * @returns Component with preview and toggle to expand the rest of the text
 */
export const ExpandWithPreview = ({ preview = 3, button, children }: ExpandProps) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);
	const [height, setHeight] = useState(0);
	const [lineHeight, setLineHeight] = useState(0);
	const boxRef = useRef<HTMLDivElement>(null);
	const lhRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!boxRef.current || !lhRef.current) {
			return;
		}

		const observer = new ResizeObserver((entries) => {
			const [height, lineHeight] = entries.map((entry) => entry.contentRect.height);

			setTimeout(() => {
				setHeight(height);
				setLineHeight(lineHeight);
			});
		});

		observer.observe(boxRef.current);
		observer.observe(lhRef.current);

		return () => observer.disconnect();
	}, []);

	return (
		<>
			<StyledBox $height={isExpanded ? height : `${preview}lh`}>
				<Box ref={boxRef}>{children}</Box>
				<Box ref={lhRef} style={{ height: '1lh' }} />
			</StyledBox>
			{height > lineHeight * preview &&
				(button ?? (
					<StyledButton
						title={isExpanded ? t('common.showLess') : t('common.showMore')}
						type="secondary"
						onClick={() => setIsExpanded(!isExpanded)}
					/>
				))}
		</>
	);
};

const StyledButton = styled(Button)`
	display: inline-block;
`;

const StyledBox = styled(Box)<{ $height: string | number }>`
	${({ $height }) => css`
		max-height: ${$height};
		overflow: hidden;
		transition: 1s ease-in-out max-height;
	`}
`;
